/**
 * Hook that bridges `push:foreground` (dispatched by pushNotifications.ts's
 * registerPush, wherever it's called from) into the `push:toast` event
 * PushToast.tsx renders. Also registers for push on mount if already
 * authenticated (e.g. app relaunched with a valid session) — a fresh login
 * instead registers via the direct registerPush() call in LoginPage.
 *
 * Usage: call once in App.tsx, unconditionally.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../modules/identity/services/auth'
import { registerPush, unregisterPush } from './pushNotifications'

export function usePushNotifications() {
  const navigate = useNavigate()

  useEffect(() => {
    // This listener must attach unconditionally, before the auth check below.
    // LoginPage.handleSubmit calls registerPush() directly on every login (so
    // re-logins on an already-mounted app still register), and that path's
    // 'pushNotificationReceived' handler dispatches 'push:foreground' the
    // same way this hook's own registerPush() call would. If this listener
    // were gated on isAuthenticated() like the registerPush() call below, a
    // cold start (fresh install or app relaunch, always unauthenticated at
    // mount) would skip attaching it — and since this effect has empty deps,
    // it never runs again, so no push would ever produce a visible toast for
    // the rest of the app's lifetime even though registration/send succeed.
    const onForeground = (e: Event) => {
      const detail = (e as CustomEvent).detail
      const title: string = detail?.title ?? 'Insurance Khareedo'
      const body: string  = detail?.body  ?? ''
      window.dispatchEvent(
        new CustomEvent('push:toast', { detail: { title, body } })
      )
    }
    window.addEventListener('push:foreground', onForeground)

    if (authService.isAuthenticated()) {
      registerPush(navigate)
    }

    return () => {
      window.removeEventListener('push:foreground', onForeground)
      unregisterPush()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
